// Sentry init for production error tracking.
//
// Setup checklist (one-time):
//   1. Create a free Sentry account at sentry.io.
//   2. Create a React project; copy the DSN it gives you.
//   3. In Vercel, set environment variable REACT_APP_SENTRY_DSN to the
//      DSN value. Apply to Production scope (and Preview if you want).
//   4. Trigger a redeploy. From then on, every JS exception in production
//      gets a stack trace + URL + auth user context posted to Sentry.
//
// If REACT_APP_SENTRY_DSN is not set (or we're running on localhost),
// Sentry init is a no-op — local dev runs silently, no events sent.
//
// Cost note: free tier covers 5k events/month + 50k performance events.
// We disable performance monitoring (tracesSampleRate: 0) and session
// replay so the only thing using quota is actual errors. Should stay
// well under the cap unless something is on fire.

import * as Sentry from '@sentry/react'

let initialized = false

export function initSentry() {
  // Only run once.
  if (initialized) return
  initialized = true

  // Stable health-check utility for the production console. Returns
  // a Sentry event id and posts a message to the Issues feed. Use:
  //   window.__SENTRY_TEST__()
  // If Sentry isn't initialized (no DSN, localhost), the call is a
  // harmless no-op (captureMessage returns undefined).
  if (typeof window !== 'undefined') {
    window.__SENTRY_TEST__ = () =>
      Sentry.captureMessage('Manual test from window.__SENTRY_TEST__')
  }

  const dsn = process.env.REACT_APP_SENTRY_DSN
  // No DSN configured → silent no-op. Lets the code ship before Sentry
  // is wired up in Vercel, and keeps local dev clean.
  if (!dsn) return

  // Don't report errors that come from localhost dev runs even if a DSN
  // happens to be set in someone's local .env. We only want production
  // signal in Sentry.
  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
  if (hostname === 'localhost' || hostname === '127.0.0.1') return

  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV || 'production',
    // Vercel injects this at build time when source-mapping is on. Used
    // as the "release" tag in Sentry so commits can be cross-referenced.
    release: process.env.REACT_APP_VERCEL_GIT_COMMIT_SHA || undefined,
    // Errors only — performance + replay disabled to stay on the free tier.
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // Drop events that are clearly noise (browser extensions, ad blockers).
    // Real issues will still get through.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
    ],
  })
}

// Tag the current Sentry scope with the signed-in user so error reports
// can be correlated. We intentionally do NOT send email or full_name —
// this is a community PWA serving participants (incl. minors' parents)
// who haven't consented to a third-party SaaS holding their PII. The
// auth user id is enough for triage; if we need a friendlier display
// later we can derive initials client-side and pass those in.
export function setSentryUser(user, profile) {
  if (!initialized) return
  if (!user) {
    Sentry.setUser(null)
    return
  }
  Sentry.setUser({
    id: user.id,
    is_coordinator: profile?.is_coordinator === true,
  })
}

// Clear the user when they sign out, so any subsequent errors aren't
// misattributed to them.
export function clearSentryUser() {
  if (!initialized) return
  Sentry.setUser(null)
}

export { Sentry }
